import React, { useEffect, useRef, useState } from "react";
import { FiRefreshCw, FiX, FiFile, FiDownload, FiTrash2 } from "react-icons/fi";
import axios from "axios";

const ManageFile = ({ isOpen, onClose }) => {
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [loadingForFile, setLoadingForFile] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedDocumentType, setSelectedDocumentType] = useState("");
  const [customDocumentType, setCustomDocumentType] = useState("");
  const fileInputRef = useRef(null);

  const documentTypes = [
    "Human Rights Policy",
    "Social Responsibility Policy and Initiative Documentation",
    "Data Privacy Policy",
    "Labor Compliance Policy",
    "Safety Performance Report",
    "Supplier Sustainability Assessment Program",
    "Anti-corruption Policy and procedures",
    "Critical Incident Risk Management Framework",
    "GHG Emissions Report and Climate Action Plan",
    "Water Management Plan and Monitoring Reports",
    "Independent Assurance Statement",
    "Other"
  ];

  const fetchUploadedFiles = async (questionId) => {
    try {
      setLoadingForFile(true);
      const targetQuestionId = localStorage.getItem("currentQuestionId");
      const res = await axios.get(`/chatbot/files?questionId=${targetQuestionId}`);
      if(res.data.success) {
        console.log("Fetched files:", res.data.files);
        setUploadedFiles(res.data.files || []);
      }
    } catch (err) {
      console.error("Error fetching files:", err);
    } finally {
      setLoadingForFile(false);
    }
  };

  // Listen for question changes
  useEffect(() => {
    const handleQuestionChange = (e) => {
      if (e.detail && e.detail.questionId !== undefined) {
        fetchUploadedFiles(e.detail.questionId);
      }
    };

    window.addEventListener("questionChanged", handleQuestionChange);
    return () => window.removeEventListener("questionChanged", handleQuestionChange);
  }, []);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate document type selection
    if (!selectedDocumentType) {
      alert("Please select a document type before uploading.");
      return;
    }

    const finalDocumentType = selectedDocumentType === "Other" ? customDocumentType : selectedDocumentType;
    
    if (selectedDocumentType === "Other" && !customDocumentType.trim()) {
      alert("Please enter a custom document type.");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("questionId", localStorage.getItem("currentQuestionId"));
    formData.append("documentType", finalDocumentType);

    try {
      setUploading(true);
      const res = await axios.post("/chatbot/upload-file", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res.data.status) {
        fetchUploadedFiles();
        alert(res.data.message);
        // Reset form
        setSelectedDocumentType("");
        setCustomDocumentType("");
      } else if (res.data.limitFlag) {
        alert(res.data.message);
      }
    } catch (err) {
      console.error("File upload failed:", err);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleFileDelete = async (id) => {
    console.log("Attempting to delete file with ID:", id);
    if (!id) {
      console.error("File ID is undefined or null");
      return;
    }
    
    if (!window.confirm("Are you sure you want to delete this file?")) {
      return;
    }

    try {
      const res = await axios.delete(`/chatbot/delete-file/${id}`);
      if (res.data.status) {
        setUploadedFiles((prev) => prev.filter((f) => f.id !== id));
        alert("File deleted successfully");
      } else {
        alert(res.data.message || "Failed to delete file");
      }
    } catch (err) {
      console.error("File delete failed:", err);
      alert("Failed to delete file. Please try again.");
    }
  };

  const getFileIcon = (fileName) => {
    const extension = fileName?.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf':
        return '📄';
      case 'doc':
      case 'docx':
        return '📝';
      case 'xls':
      case 'xlsx':
        return '📊';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return '🖼️';
      default:
        return '📎';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'failed':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'processing':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'completed':
        return 'text-green-600 bg-green-50 border-green-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  useEffect(() => {
    if (isOpen) {
      fetchUploadedFiles();
      // Reset form when modal opens
      setSelectedDocumentType("");
      setCustomDocumentType("");
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
      <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-hidden rounded-xl shadow-2xl flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-green-600 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                <FiFile className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Document Manager</h2>
                <p className="text-green-100 text-sm">Upload and manage your ESG documents</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={fetchUploadedFiles}
                disabled={loadingForFile}
                className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors disabled:opacity-50"
                title="Refresh files"
              >
                {loadingForFile ? (
                  <svg className="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l5-5-5-5v4a10 10 0 00-10 10h4z" />
                  </svg>
                ) : (
                  <FiRefreshCw className="w-5 h-5 text-white" />
                )}
              </button>
              <button
                onClick={onClose}
                className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
              >
                <FiX className="w-5 h-5 text-white" />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Upload Section */}
          <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-lg border border-gray-200 p-5 mb-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              <FiFile className="w-5 h-5 mr-2 text-blue-600" />
              Upload New Document
            </h3>
            
            {/* Document Type Selection */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Document Type *
                </label>
                <select
                  value={selectedDocumentType}
                  onChange={(e) => setSelectedDocumentType(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                >
                  <option value="">Select document type</option>
                  {documentTypes.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              {selectedDocumentType === "Other" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Custom Document Type *
                  </label>
                  <input
                    type="text"
                    value={customDocumentType}
                    onChange={(e) => setCustomDocumentType(e.target.value)}
                    placeholder="Enter document type..."
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  />
                </div>
              )}

              {/* File Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select File *
                </label>
                <input
                  type="file"
                  onChange={handleFileUpload}
                  ref={fileInputRef}
                  className="w-full p-3 border border-gray-300 rounded-lg bg-white file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 transition-colors"
                  disabled={!selectedDocumentType || uploading}
                />
              </div>

              {uploading && (
                <div className="flex items-center space-x-2 text-blue-600">
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l5-5-5-5v4a10 10 0 00-10 10h4z" />
                  </svg>
                  <span className="text-sm">Uploading document...</span>
                </div>
              )}
            </div>
          </div>

          {/* Uploaded Files Section */}
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              <FiDownload className="w-5 h-5 mr-2 text-green-600" />
              Uploaded Documents ({uploadedFiles.length})
            </h3>

            {uploadedFiles.length === 0 ? (
              <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
                <FiFile className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500 text-sm">No documents uploaded yet</p>
                <p className="text-gray-400 text-xs mt-1">Upload your first document to get started</p>
              </div>
            ) : (
              <div className="space-y-3">
                {uploadedFiles.map((file, index) => (
                  <div
                    key={file.id || index}
                    className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3 flex-1 min-w-0">
                        <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                          <span className="text-lg">{getFileIcon(file.fileName)}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 mb-1">
                            <h4 className="font-medium text-gray-800 truncate text-sm">
                              {file.fileName}
                            </h4>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(file.status)}`}>
                              {file.status || 'unknown'}
                            </span>
                          </div>
                          
                          <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                            <span>Active: {file.isActive ? "Yes" : "No"}</span>
                          </div>
                          
                          {file.uploadedAt && (
                            <p className="text-xs text-gray-400 mt-1">
                              Uploaded: {new Date(file.uploadedAt).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2 ml-4 flex-shrink-0">
                        {file.id ? (
                          <button
                            onClick={() => handleFileDelete(file.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete file"
                          >
                            <FiTrash2 className="w-4 h-4" />
                          </button>
                        ) : (
                          <span className="text-gray-400 text-xs" title="Cannot delete - missing file ID">
                            <FiTrash2 className="w-4 h-4" />
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageFile;